import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { FormQuestionCodeEnum } from 'src/app/models/enums/form-question-code-enum';
import { StatementBooleanAnswerEnum } from 'src/app/models/enums/statement-answer-enum';
import { WorkerFormBasicPersonalData } from 'src/app/models/worker-form-basic-personal-data';
import { WorkerFormStatementDto } from 'src/app/models/worker-form-statement-dto';
import { PdfViewerService } from 'src/app/shared/services/pdf-viewer.service';
import {
  GenerateForeignWorkerDeclarationComponent,
  GenerateForeignWorkerDeclarationModalData,
} from '../../worker-profile/documents-section/generate-foreign-worker-declaration';
import {
  GenerateHolidayChildCareStatementComponent,
  GenerateHolidayChildCareStatementModalData,
} from '../../worker-profile/documents-section/generate-holiday-child-care-statement';
import { GeneratePIT2ModalComponent, GeneratePIT2ModalData } from '../../worker-profile/documents-section/generate-pit2.component/generate-pit2.component';
import {
  GeneratePpkCancellationStatementComponent,
  GeneratePpkCancellationStatementModalData,
} from '../../worker-profile/documents-section/generate-ppk-cancellation-statement';
import { getAnswerCodeByQuestionCode } from './statement-change-detection';

export interface StatementDialogContext {
  workerId: number;
  workerFormId: number;
  personalData: WorkerFormBasicPersonalData;
}

export interface GeneratedDocumentResult {
  IsSuccess: boolean;
  Value: number;
}

@Injectable({ providedIn: 'root' })
export class StatementDocumentDialogsService {
  private readonly dialog = inject(MatDialog);
  private readonly pdfViewerService = inject(PdfViewerService);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);

  openPit2(context: StatementDialogContext, statements: WorkerFormStatementDto[]): Promise<GeneratedDocumentResult | undefined> {
    return this.open<GeneratePIT2ModalComponent, GeneratePIT2ModalData, GeneratedDocumentResult>(GeneratePIT2ModalComponent, {
      Title: 'PIT2',
      WorkerId: context.workerId,
      WorkerFormId: context.workerFormId,
      FirstName: context.personalData.FirstName,
      LastName: context.personalData.LastName,
      Pesel: context.personalData.Pesel,
      DateOfBirth: context.personalData.DateOfBirth,
      UpdateStatements: true,
      Statements: statements,
    });
  }

  openPpkCancellation(context: StatementDialogContext): Promise<number | undefined> {
    return this.open<GeneratePpkCancellationStatementComponent, GeneratePpkCancellationStatementModalData, number>(
      GeneratePpkCancellationStatementComponent,
      {
        WorkerId: context.workerId,
        WorkerFormId: context.workerFormId,
        FirstName: context.personalData.FirstName,
        LastName: context.personalData.LastName,
        Pesel: context.personalData.Pesel,
        DateOfBirth: context.personalData.DateOfBirth,
      } as GeneratePpkCancellationStatementModalData, // Title intentionally omitted (as before the extraction)
    );
  }

  openHolidayChildCare(context: StatementDialogContext, statements: WorkerFormStatementDto[]): Promise<number | undefined> {
    return this.open<GenerateHolidayChildCareStatementComponent, GenerateHolidayChildCareStatementModalData, number>(
      GenerateHolidayChildCareStatementComponent,
      {
        Title: this.translateService.instant('HolidayChildCareStatemenModalTitle'),
        WorkerId: context.workerId,
        WorkerFormId: context.workerFormId,
        FirstName: context.personalData.FirstName,
        LastName: context.personalData.LastName,
        Pesel: context.personalData.Pesel,
        DateOfBirth: context.personalData.DateOfBirth,
        IsHolidayChildcareAnswerCode: getAnswerCodeByQuestionCode(statements, FormQuestionCodeEnum.IsHolidayChildcare) ?? StatementBooleanAnswerEnum.NO,
        HolidayChildcareAnswerCode: getAnswerCodeByQuestionCode(statements, FormQuestionCodeEnum.HolidayChildcare) ?? StatementBooleanAnswerEnum.NO,
      },
    );
  }

  openForeignWorkerDeclaration(context: StatementDialogContext, statements: WorkerFormStatementDto[]): Promise<number | undefined> {
    return this.open<GenerateForeignWorkerDeclarationComponent, GenerateForeignWorkerDeclarationModalData, number>(
      GenerateForeignWorkerDeclarationComponent,
      {
        Title: this.translateService.instant('ForeignWorkerDeclarationModalTitle'),
        WorkerId: context.workerId,
        WorkerFormId: context.workerFormId,
        FirstName: context.personalData.FirstName,
        LastName: context.personalData.LastName,
        Pesel: context.personalData.Pesel,
        DateOfBirth: context.personalData.DateOfBirth,
        FutureInPolandAnswerCode: getAnswerCodeByQuestionCode(statements, FormQuestionCodeEnum.FutureInPoland) ?? '',
        ResidentRP183DaysOfTaxYearAnswerCode: getAnswerCodeByQuestionCode(statements, FormQuestionCodeEnum.ResidentRP183DaysOfTaxYear) ?? '',
      },
    );
  }

  previewPit2(workerId: number, documentId: number): Promise<unknown> {
    let firstName: string;
    let lastName: string;

    this.authService
      .getUser()
      .pipe(first())
      .subscribe((user) => {
        firstName = user.firstName;
        lastName = user.lastName;
      });

    return this.preview(workerId, documentId, `Oświadczenie PIT 2 ${firstName} ${lastName}.pdf`);
  }

  previewPpkCancellation(workerId: number, documentId: number): Promise<unknown> {
    return this.previewIfGenerated(workerId, documentId, 'PpkCancellationStatemenfFileName');
  }

  previewHolidayChildCare(workerId: number, documentId: number): Promise<unknown> {
    return this.previewIfGenerated(workerId, documentId, 'HolidayChildCareStatemenfFileName');
  }

  previewForeignWorkerDeclaration(workerId: number, documentId: number): Promise<unknown> {
    return this.previewIfGenerated(workerId, documentId, 'ForeignWorkerDeclarationFileName');
  }

  private previewIfGenerated(workerId: number, documentId: number, fileNameTranslationKey: string): Promise<unknown> {
    if (!documentId) return Promise.resolve();
    return this.preview(workerId, documentId, `${this.translateService.instant(fileNameTranslationKey)}.pdf`);
  }

  private preview(workerId: number, documentId: number, fileName: string): Promise<unknown> {
    return firstValueFrom(
      this.pdfViewerService
        .show({
          Endpoint: `workers/${workerId}/files`,
          FileId: documentId,
          FileName: fileName,
        })
        .afterClosed(),
      { defaultValue: undefined },
    );
  }

  private open<TComponent, TData, TResult>(component: ComponentType<TComponent>, data: TData): Promise<TResult | undefined> {
    const dialogConfig = new MatDialogConfig<TData>();
    dialogConfig.panelClass = 'document-form-dialog';
    dialogConfig.data = data;

    return firstValueFrom(this.dialog.open<TComponent, TData, TResult>(component, dialogConfig).afterClosed(), { defaultValue: undefined });
  }
}
